import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useSnackbar from '../hooks/useSnackbar'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useFrameworkLibrary from '../hooks/useFrameworkLibrary'
import useFrameSuggestions, { FrameSuggestion } from '../hooks/useFrameSuggestions'
import BackLink from '../components/system/BackLink'
import DisabledReason from '../components/system/DisabledReason'
import { refuse } from '../utils/permissionReason'
import { extractErrorMessage } from '../utils/apitools'
import { daysAgo, RecodeEstimate, sinceFromDate, sumEstimates } from '../utils/dynamicFrames'

const formatDate = (ms: number | string) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * The suggestions a dynamic code frame has collected, and a Manager's
 * decisions on them: Accept (the code is added at the end of the frame and
 * reaches every linked pipeline), Merge into an existing code, Rename, or
 * Reject. Nothing changes a frame without a click here
 * (docs/autocoder-dynamic-frames.md).
 */
const FrameSuggestionsPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const snackbar = useSnackbar()
  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const frameworkId = router.params.frameworkId || ''

  const who = useWhoToAsk(account.customer?.name || customer)
  const isManager = Boolean(account.user?.role_permissions?.isManager)
  const managerReason = isManager ? undefined : refuse("You can't decide on a code frame's suggestions.", who).reason

  const { framework, frameworkLoading, setFrameworkDynamic, dynamicLoading } = useFrameworkLibrary({ customer, job, frameworkId })
  const s = useFrameSuggestions(customer, job, frameworkId)

  const [view, setView] = useState<'pending' | 'decided'>('pending')
  const [acceptTarget, setAcceptTarget] = useState<FrameSuggestion | null>(null)
  const [renameTarget, setRenameTarget] = useState<FrameSuggestion | null>(null)
  const [mergeTarget, setMergeTarget] = useState<FrameSuggestion | null>(null)

  const codes = framework?.data?.main_variable?.codes ?? []
  const pending = s.suggestions.filter(x => x.status === 'pending')
  const decided = s.suggestions.filter(x => x.status !== 'pending')
  const shown = view === 'pending' ? pending : decided
  const codeText = (code?: string) => codes.find(c => c.code === code)?.text

  const goBack = () => router.navigate('autocoder', { customer, job, tab: 'frameworks' })

  if (frameworkLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3, height: 'calc(100vh - 84px)', overflow: 'auto' }}>
      <BackLink label="Back to code frames" onClick={goBack} />
      <Paper elevation={1} sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>Suggestions: {framework?.name || 'Code frame'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Version {framework?.version ?? 1} · {codes.length} codes · {pending.length} pending. New codes are added at the end of the frame and reach every pipeline that uses it.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DisabledReason reason={managerReason}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(framework?.dynamic)}
                  disabled={dynamicLoading || !framework}
                  onChange={(_, checked) => framework && setFrameworkDynamic(framework.id, checked)}
                  inputProps={{ 'aria-label': 'Dynamic' }}
                />
              }
              label="Dynamic"
            />
          </DisabledReason>
          <DisabledReason reason={managerReason ?? (pending.length < 2 ? 'There are fewer than two pending suggestions to group.' : undefined)}>
            <Button
              variant="outlined"
              color="info"
              startIcon={s.consolidating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              disabled={s.consolidating}
              onClick={() => s.consolidate().catch(() => undefined)}
            >
              Group look-alikes now
            </Button>
          </DisabledReason>
        </Box>
      </Paper>

      {framework && !framework.dynamic && (
        <Alert severity="info">This code frame is not dynamic, so it collects no new suggestions. Switch Dynamic on to start collecting them.</Alert>
      )}

      <Box>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="pending">Pending ({pending.length})</ToggleButton>
          <ToggleButton value="decided">Decided ({decided.length})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {s.suggestionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : s.suggestionsError ? (
        <Alert severity="error">{extractErrorMessage(s.suggestionsError)}</Alert>
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {view === 'pending' ? 'No pending suggestions. New ones arrive as comments are coded.' : 'No decisions yet.'}
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" data-testid="frame-suggestions-table">
            <TableHead>
              <TableRow>
                <TableCell>Suggestion</TableCell>
                <TableCell align="right">Times suggested</TableCell>
                <TableCell>Examples</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell align="right">{view === 'pending' ? 'Actions' : 'Decision'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(sg => (
                <TableRow key={sg.id} hover data-testid={`frame-suggestion-${sg.id}`}>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" fontWeight={500}>{sg.label}</Typography>
                    {sg.description && <Typography variant="caption" color="text.secondary">{sg.description}</Typography>}
                  </TableCell>
                  <TableCell align="right">{sg.count}</TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    {sg.examples.map((e, i) => (
                      <Typography key={`${e.record_id}-${i}`} variant="caption" component="div" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        “{e.text.length > 140 ? `${e.text.slice(0, 140)}…` : e.text}”
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(sg.last_seen)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {view === 'pending' ? (
                      <>
                        <DisabledReason reason={managerReason}>
                          <Button size="small" variant="contained" color="info" startIcon={<CheckIcon />} disabled={s.busy} onClick={() => setAcceptTarget(sg)}>
                            Accept
                          </Button>
                        </DisabledReason>
                        <DisabledReason reason={managerReason}>
                          <Tooltip title="Merge into…">
                            <IconButton aria-label="Merge into…" disabled={s.busy} onClick={() => setMergeTarget(sg)}><CallMergeIcon /></IconButton>
                          </Tooltip>
                        </DisabledReason>
                        <DisabledReason reason={managerReason}>
                          <Tooltip title="Rename">
                            <IconButton aria-label="Rename" disabled={s.busy} onClick={() => setRenameTarget(sg)}><EditIcon /></IconButton>
                          </Tooltip>
                        </DisabledReason>
                        <DisabledReason reason={managerReason}>
                          <Tooltip title="Reject">
                            <IconButton aria-label="Reject" disabled={s.busy} onClick={() => s.reject(sg.id).catch(() => undefined)}><CloseIcon /></IconButton>
                          </Tooltip>
                        </DisabledReason>
                      </>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={sg.status === 'accepted' ? 'success' : 'default'}
                        label={
                          sg.status === 'accepted' ? `Accepted as code ${sg.accepted_code}`
                            : sg.status === 'merged' && sg.accepted_code ? `Merged into ${codeText(sg.accepted_code) || `code ${sg.accepted_code}`}`
                              : sg.status === 'merged' ? 'Grouped with another suggestion'
                                : 'Rejected'
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {s.versions.length > 1 && (
        <Paper elevation={0} sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>
          {[...s.versions].reverse().map(v => (
            <Typography key={v.version} variant="body2" color="text.secondary">
              Version {v.version} · {formatDate(v.createdAt)} · {v.addedCodes.length ? `added ${v.addedCodes.map(c => `${c.code} ${c.text}`).join(', ')}` : 'as made'}{v.createdBy ? ` · ${v.createdBy}` : ''}
            </Typography>
          ))}
        </Paper>
      )}

      {acceptTarget && (
        <AcceptDialog
          suggestion={acceptTarget}
          linkedCount={s.linked.length}
          onClose={() => setAcceptTarget(null)}
          estimate={async (since) => sumEstimates(await Promise.all(s.linked.map(l => s.estimateRecode(l, since))))}
          onAccept={async (input) => {
            const res = await s.accept(acceptTarget.id, input)
            const reached = res.pipelines.filter(p => p.updated)
            if (input.mode === 'recode' && input.since) {
              let started = 0
              for (const p of reached) {
                try {
                  const r = await s.startRecode(p.pipeline_id, p.framework, input.since)
                  started += r.records
                } catch (err) {
                  snackbar.error(`The code was added, but re-coding ${p.name || p.pipeline_id} did not start: ${extractErrorMessage(err)}`)
                }
              }
              snackbar.success(`Added code ${res.code.code} “${res.code.text}”; re-coding ${started} comments`)
            } else {
              snackbar.success(`Added code ${res.code.code} “${res.code.text}” to ${reached.length} pipeline${reached.length === 1 ? '' : 's'}`)
            }
            const skipped = res.pipelines.filter(p => p.skipped)
            if (skipped.length) snackbar.warning(`Not added to ${skipped.map(p => p.name || p.pipeline_id).join(', ')}: ${skipped[0].skipped}`)
            setAcceptTarget(null)
          }}
        />
      )}

      {renameTarget && (
        <RenameDialog
          suggestion={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSave={async (label, description) => {
            await s.rename(renameTarget.id, label, description)
            setRenameTarget(null)
          }}
        />
      )}

      {mergeTarget && (
        <MergeDialog
          suggestion={mergeTarget}
          codes={codes}
          onClose={() => setMergeTarget(null)}
          onMerge={async (code) => {
            await s.merge(mergeTarget.id, code)
            setMergeTarget(null)
          }}
        />
      )}
    </Box>
  )
}

interface AcceptDialogProps {
  suggestion: FrameSuggestion
  linkedCount: number
  onClose: () => void
  estimate: (since: string) => Promise<Omit<RecodeEstimate, 'model'>>
  onAccept: (input: { label: string; description: string; mode: 'forward' | 'recode'; since?: string }) => Promise<void>
}

export const AcceptDialog: React.FC<AcceptDialogProps> = ({ suggestion, linkedCount, onClose, estimate, onAccept }) => {
  const [label, setLabel] = useState(suggestion.label)
  const [description, setDescription] = useState(suggestion.description)
  const [mode, setMode] = useState<'forward' | 'recode'>('forward')
  const [date, setDate] = useState(daysAgo(30))
  const [est, setEst] = useState<Omit<RecodeEstimate, 'model'> | null>(null)
  const [estError, setEstError] = useState('')
  const [estimating, setEstimating] = useState(false)
  const [saving, setSaving] = useState(false)

  const since = sinceFromDate(date)
  const runEstimate = async () => {
    if (!since) return
    setEstimating(true)
    setEstError('')
    setEst(null)
    try {
      setEst(await estimate(since))
    } catch (err) {
      setEstError(extractErrorMessage(err))
    } finally {
      setEstimating(false)
    }
  }

  const recodeBlocked = mode === 'recode' && (!since || !est)
  const submitReason = !label.trim() ? 'The code needs a label.' : recodeBlocked ? 'See the estimate first.' : undefined

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Accept “{suggestion.label}”</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Label" value={label} onChange={e => setLabel(e.target.value)} required fullWidth />
        <TextField label="Description (for the model)" value={description} onChange={e => setDescription(e.target.value)} multiline minRows={2} fullWidth />
        <Typography variant="body2" color="text.secondary">
          The code is added at the end of the frame, as a new version, and reaches {linkedCount} pipeline{linkedCount === 1 ? '' : 's'} that use{linkedCount === 1 ? 's' : ''} it.
        </Typography>
        <RadioGroup value={mode} onChange={(_, v) => { setMode(v as 'forward' | 'recode'); setEst(null) }}>
          <FormControlLabel value="forward" control={<Radio />} label="From now on: comments coded from now on can get the new code" />
          <FormControlLabel value="recode" control={<Radio />} label="Re-code since a date: code this frame again for comments since then" />
        </RadioGroup>
        {mode === 'recode' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 4 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                type="date"
                size="small"
                label="Re-code since"
                value={date}
                onChange={e => { setDate(e.target.value); setEst(null) }}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" color="info" onClick={runEstimate} disabled={!since || estimating || linkedCount === 0}>
                {estimating ? 'Estimating…' : 'Estimate'}
              </Button>
            </Box>
            {linkedCount === 0 && <Alert severity="info">No pipeline uses this frame yet, so there is nothing to re-code.</Alert>}
            {estError && <Alert severity="error">The estimate failed: {estError}</Alert>}
            {est && (
              <Alert severity="info">
                Re-codes {est.records.toLocaleString()} comments with this frame only, about {est.estimatedCredits.toLocaleString(undefined, { maximumFractionDigits: 2 })} credits ({est.estimatedTokens.toLocaleString()} tokens).
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info">Cancel</Button>
        <DisabledReason reason={submitReason}>
          <Button
            variant="contained"
            color="info"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              try {
                await onAccept({ label: label.trim(), description: description.trim(), mode, since: mode === 'recode' ? since : undefined })
              } catch {
                // the hook has shown the error
              } finally {
                setSaving(false)
              }
            }}
          >
            {mode === 'recode' ? 'Accept and re-code' : 'Accept'}
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

const RenameDialog: React.FC<{ suggestion: FrameSuggestion; onClose: () => void; onSave: (label: string, description: string) => Promise<void> }> = ({ suggestion, onClose, onSave }) => {
  const [label, setLabel] = useState(suggestion.label)
  const [description, setDescription] = useState(suggestion.description)
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename suggestion</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Label" value={label} onChange={e => setLabel(e.target.value)} required fullWidth />
        <TextField label="Description (for the model)" value={description} onChange={e => setDescription(e.target.value)} multiline minRows={2} fullWidth />
        <Typography variant="caption" color="text.secondary">A label another pending suggestion already has joins the two into one.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info">Cancel</Button>
        <DisabledReason reason={label.trim() ? undefined : 'The suggestion needs a label.'}>
          <Button variant="contained" color="info" onClick={() => onSave(label.trim(), description.trim()).catch(() => undefined)}>Save</Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

const MergeDialog: React.FC<{ suggestion: FrameSuggestion; codes: { code: string; text: string }[]; onClose: () => void; onMerge: (code: string) => Promise<void> }> = ({ suggestion, codes, onClose, onMerge }) => {
  const [code, setCode] = useState('')
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Merge “{suggestion.label}” into…</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <TextField select fullWidth size="small" label="Existing code" value={code} onChange={e => setCode(e.target.value)}>
          {codes.map(c => <MenuItem key={c.code} value={c.code}>{c.code} · {c.text}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The suggestion is recorded as meaning this code. The frame does not change.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info">Cancel</Button>
        <DisabledReason reason={code ? undefined : 'Pick the code it means.'}>
          <Button variant="contained" color="info" onClick={() => onMerge(code).catch(() => undefined)}>Merge</Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

export default FrameSuggestionsPage
